import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';

/** Stable empties to avoid creating new references */
const EMPTY_ARRAY: any[] = [];
const EMPTY_STRING = '';
const EMPTY_NAME = Object.freeze({ firstName: '', lastName: '' });
const EMPTY_ADDRESS = Object.freeze({ addressLine1: '', addressLine2: '' });
const EMPTY_CITY_POSTCODE = Object.freeze({ city: '', postcode: '' });
const EMPTY_SELECTED_LOCATION = Object.freeze({ selectedCountry: EMPTY_STRING, selectedProvince: EMPTY_STRING });
const EMPTY_PHONE_EMOJI = Object.freeze({ phoneCode: EMPTY_STRING, emoji: EMPTY_STRING });

const selectLocationState = (state: RootState) => state.location;
export const selectSelectedAddress = (state: RootState) => state.user.selectedAddress;
export const selectShippingArray = (state: RootState) => state.user.shipping;

/** Names (memoized by shipping reference) */
export const selectShippingNames = createSelector([selectShippingArray], (shipping) => {
  if (!shipping) return EMPTY_NAME;
  const firstName = shipping.firstName ?? EMPTY_STRING;
  const lastName = shipping.lastName ?? EMPTY_STRING;
  // If both empty, return a shared object to keep reference stable
  if (!firstName && !lastName) return EMPTY_NAME;
  return { firstName, lastName };
});

/** Address lines */
export const selectShippingAddress = createSelector([selectShippingArray], (shipping) => {
  if (!shipping) return EMPTY_ADDRESS;
  const addressLine1 = shipping.addressLine1 ?? EMPTY_STRING;
  const addressLine2 = shipping.addressLine2 ?? EMPTY_STRING;
  if (!addressLine1 && !addressLine2) return EMPTY_ADDRESS;
  return { addressLine1, addressLine2 };
});

/** City & Postcode */
export const selectCityPostcode = createSelector([selectShippingArray], (shipping) => {
  if (!shipping) return EMPTY_CITY_POSTCODE;
  const city = shipping.city ?? EMPTY_STRING;
  const postcode = shipping.postcode ?? EMPTY_STRING;
  if (!city && !postcode) return EMPTY_CITY_POSTCODE;
  return { city, postcode };
});

/** Selected country/province: prefer location slice, fallback to shipping */
export const selectCombinedShippingLocation = createSelector(
  [
    selectShippingArray,
    (state: RootState) => selectLocationState(state).selectedCountry,
    (state: RootState) => selectLocationState(state).selectedProvince,
  ],
  (shipping, selectedCountryFromLocation, selectedProvinceFromLocation) => {
    const selectedCountryFromShipping = shipping?.country ?? EMPTY_STRING;
    const selectedProvinceFromShipping = shipping?.province ?? EMPTY_STRING;

    const selectedCountry = selectedCountryFromLocation ?? selectedCountryFromShipping ?? EMPTY_STRING;
    const selectedProvince = selectedProvinceFromLocation ?? selectedProvinceFromShipping ?? EMPTY_STRING;

    if (!selectedCountry && !selectedProvince) return EMPTY_SELECTED_LOCATION;
    return { selectedCountry, selectedProvince };
  }
);

/** User-provided phone code + emoji from shipping */
export const selectUserPhoneCodeAndEmoji = createSelector([selectShippingArray], (shipping) => {
  if (!shipping) return EMPTY_PHONE_EMOJI;
  const phoneCode = shipping.phoneCode ?? EMPTY_STRING;
  const emoji = shipping.emoji ?? EMPTY_STRING;
  if (!phoneCode && !emoji) return EMPTY_PHONE_EMOJI;
  return { phoneCode, emoji };
});

/** Location-derived phone code + emoji (memoized) */
export const selectLocationPhoneCodeAndEmoji = createSelector(
  [
    selectShippingArray,
    (state: RootState) => selectLocationState(state).selectedCountry,
    (state: RootState) => selectLocationState(state).countries ?? EMPTY_ARRAY,
    (state: RootState) => selectLocationState(state).provinces ?? EMPTY_ARRAY,
  ],
  (shipping, selectedCountryFromLocation, countries, provinces) => {
    const selectedCountry = selectedCountryFromLocation ?? shipping?.country ?? EMPTY_STRING;

    if (!selectedCountry) return EMPTY_PHONE_EMOJI;

    const country = countries.find((item) => item.name === selectedCountry);
    const province = provinces.find((prov) => prov.country?.id === country?.id);
    const phoneCode = country?.phone_code ?? province?.country?.phone_code ?? EMPTY_STRING;
    const emoji = country?.emoji ?? province?.country?.emoji ?? EMPTY_STRING;

    if (!phoneCode && !emoji) return EMPTY_PHONE_EMOJI;
    return { phoneCode, emoji };
  }
);

/** Final merged phone code + emoji (location wins over user) */
export const selectPhoneCodeAndEmoji = createSelector(
  [selectUserPhoneCodeAndEmoji, selectLocationPhoneCodeAndEmoji],
  (userPhone, locationPhone) => {
    const phoneCode = locationPhone.phoneCode || userPhone.phoneCode || EMPTY_STRING;
    const emoji = locationPhone.emoji || userPhone.emoji || EMPTY_STRING;

    if (!phoneCode && !emoji) return EMPTY_PHONE_EMOJI;
    if (phoneCode === userPhone.phoneCode && emoji === userPhone.emoji) return userPhone;
    if (phoneCode === locationPhone.phoneCode && emoji === locationPhone.emoji) return locationPhone;
    return { phoneCode, emoji };
  }
);

/** Contact settings */
export const selectShippingContact = createSelector([selectShippingArray], (shipping) => {
  if (!shipping) {
    return { useAddressForBilling: false, telephone: EMPTY_STRING };
  }
  const useAddressForBilling = !!shipping.useAddressForBilling;
  const telephone = shipping.telephone ?? EMPTY_STRING;
  if (!useAddressForBilling && !telephone) return { useAddressForBilling: false, telephone: EMPTY_STRING };
  return { useAddressForBilling, telephone };
});
